import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import axios from "axios";
import { Toaster } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { auth as authApi } from "@/lib/api";

// Global 401 interceptor for the RAW `axios` module (many pages import
// it directly instead of the shared `api` instance in `lib/api.js`).
// Without this, an expired session shows a "Missing token" toast on
// every list-price approver page with no way to recover except closing
// the tab. Installed once at app boot — idempotent even under HMR.
if (!axios.__prsGlobal401Installed) {
  axios.interceptors.response.use(
    (r) => r,
    (err) => {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "";
      if (status === 401
          || /Missing token|Invalid token/i.test(detail)) {
        try {
          localStorage.removeItem("pr_token");
          localStorage.removeItem("pr_user");
        } catch { /* noop */ }
        if (!window.location.pathname.includes("/login")) {
          window.location.href = "/login";
        }
      }
      return Promise.reject(err);
    },
  );
  axios.__prsGlobal401Installed = true;
}

import Login from "@/pages/Login";
import Home from "@/pages/Home";
import Dashboard from "@/pages/Dashboard";
import Admin from "@/pages/Admin";
import CreatePR from "@/pages/CreatePR";
import MyPRs from "@/pages/MyPRs";
import PRReview from "@/pages/PRReview";
import Pipeline from "@/pages/Pipeline";
import PriceExplorer from "@/pages/PriceExplorer";
import SupplierPriceList from "@/pages/SupplierPriceList";
import EmailAction from "@/pages/EmailAction";
import AdminEmailLog from "@/pages/AdminEmailLog";
import SapData from "@/pages/SapData";
import ListPrices from "@/pages/ListPrices";
import AutoApproveReport from "@/pages/AutoApproveReport";
import ListPriceApprovals from "@/pages/ListPriceApprovals";
import MRPProposals from "@/pages/MRPProposals";
import AdminBuyerAssignments from "@/pages/AdminBuyerAssignments";
import SapHub from "@/pages/SapHub";
import SapHealth from "@/pages/SapHealth";
import Fastpath from "@/components/Fastpath";
import ErpSignal from "@/components/ErpSignal";
import "@/App.css";

function Protected({ children }) {
  const t = localStorage.getItem("pr_token");
  return t ? children : <Navigate to="/login" replace />;
}

function ApproverOnly({ children }) {
  const t = localStorage.getItem("pr_token");
  if (!t) return <Navigate to="/login" replace />;
  let u = {};
  try { u = JSON.parse(localStorage.getItem("pr_user") || "{}"); } catch { /* ignore */ }
  // Trust the auto-detected flag set by Home.jsx on first load. Fall back to role
  // check so this works even before /me/access has been fetched (first navigation
  // after login). Admins always pass.
  const roleAllows = ["Authoriser", "Approver", "Both", "Admin"].includes(u.role || "");
  const detected = u.has_approval_access;
  const canApprove = (detected === undefined) ? roleAllows : !!detected;
  return canApprove ? children : <Navigate to="/home" replace />;
}

function FastpathHost() {
  const loc = useLocation();
  if (loc.pathname === "/login") return null;
  return <Fastpath />;
}

function App() {
  // Fire once React has mounted so the HTML splash in index.html can fade
  // out instantly instead of waiting on its 3-second minimum. Uses a double
  // rAF to guarantee the first paint has landed on-screen.
  useEffect(() => {
    let raf1, raf2;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        try { window.dispatchEvent(new Event("app-ready")); } catch { /* noop */ }
      });
    });
    return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); };
  }, []);

  // Silent JWT refresh on every app boot. Users open this PWA sporadically —
  // once in the morning, then again after lunch, maybe again in the evening.
  // With a 12h JWT the afternoon open would kick them to /login. With 30-day
  // JWTs plus this on-boot refresh, an active user's token sliding-window
  // extends indefinitely; only a truly-idle user (30 days silent) ever hits
  // /login again. If we already have no token the call is skipped — the
  // Protected wrapper will bounce them to /login anyway.
  useEffect(() => {
    const tok = localStorage.getItem("pr_token");
    if (!tok) return;
    authApi.refresh()
      .then(({ data }) => {
        if (data && data.token) {
          authApi.setSession(data.token, data.user || JSON.parse(
            localStorage.getItem("pr_user") || "{}"
          ));
        }
      })
      .catch(() => { /* 401 is handled by the axios interceptor -> /login */ });
  }, []);

  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <TooltipProvider delayDuration={200}>
        <BrowserRouter>
          <FastpathHost />
          <ErpSignal />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/home" element={<Protected><Home /></Protected>} />
            <Route path="/" element={<ApproverOnly><Dashboard /></ApproverOnly>} />
            <Route path="/admin" element={<Protected><Admin /></Protected>} />
            <Route path="/create" element={<Protected><CreatePR /></Protected>} />
            <Route path="/pr/edit/:vocNo" element={<Protected><CreatePR /></Protected>} />
            <Route path="/my-prs" element={<Protected><MyPRs /></Protected>} />
            <Route path="/pipeline" element={<Protected><Pipeline /></Protected>} />
            <Route path="/price-explorer" element={<Protected><PriceExplorer /></Protected>} />
            <Route path="/supplier-price-list" element={<Protected><SupplierPriceList /></Protected>} />
            {/* Email one-click Approve/Reject landing. INTENTIONALLY not
                wrapped in <Protected> — the /email-action/preview endpoint
                itself decides whether the user needs to log in first, based
                on the device cookie. This lets the "primary recipient"
                one-click experience work without a login gate. */}
            <Route path="/action/:voc" element={<EmailAction />} />
            {/* HIDDEN admin telemetry — no nav link. Only reachable by
                typing the URL directly. Backend also enforces Admin role
                on /api/admin/email-log so a non-admin who guesses this
                URL still sees an "Admin access only" screen. */}
            <Route path="/admin/email-log" element={<Protected><AdminEmailLog /></Protected>} />
            <Route path="/admin/sap-data" element={<Protected><SapData /></Protected>} />
            <Route path="/admin/list-prices" element={<Protected><ListPrices /></Protected>} />
            <Route path="/reports/auto-approve-prs" element={<Protected><AutoApproveReport /></Protected>} />
            <Route path="/admin/sap-health" element={<Protected><SapHealth /></Protected>} />
            <Route path="/admin/list-price-approvals" element={<Protected><ListPriceApprovals /></Protected>} />
            <Route path="/admin/list-price-history" element={<Protected><ListPriceApprovals /></Protected>} />
            <Route path="/mrp-proposals" element={<Protected><MRPProposals /></Protected>} />
            <Route path="/admin/buyer-assignments" element={<Protected><AdminBuyerAssignments /></Protected>} />
            <Route path="/sap" element={<Protected><SapHub /></Protected>} />
            {process.env.REACT_APP_ENABLE_REQUESTOR_REVIEW === "true" && (
              <Route path="/pr-review" element={<Protected><PRReview /></Protected>} />
            )}
            <Route path="*" element={<Navigate to="/home" replace />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </div>
  );
}

export default App;
